import { localDB } from '../crud/localDB/localDB';
import { NotificationTopic, NotificationSettings } from '../views/Base/components/NavBar/components/HeaderProfile/components/NotificationCenter/components/NotificationConfig/NotificationConfig';

// Simple notification beep using Web Audio API
const createNotificationBeep = (volume: number): void => {
  const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
  
  // Create oscillator for the beep
  const oscillator = audioContext.createOscillator();
  const gainNode = audioContext.createGain();
  
  oscillator.connect(gainNode);
  gainNode.connect(audioContext.destination);
  
  // Configure the beep sound
  oscillator.frequency.value = 800; // Frequency in Hz (pleasant notification tone)
  oscillator.type = 'sine'; // Sine wave for smooth sound
  
  // Set volume
  gainNode.gain.setValueAtTime(volume, audioContext.currentTime);
  gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.2);
  
  // Play the beep
  oscillator.start(audioContext.currentTime);
  oscillator.stop(audioContext.currentTime + 0.2);
};

class NotificationSoundManager {
  private audio: HTMLAudioElement | null = null;
  private settings: NotificationSettings | null = null;

  constructor() {
    this.loadSettings();
  }

  private async loadSettings(): Promise<void> {
    try {
      const [enabled, volume, topicSettings] = await Promise.all([
        localDB.get<boolean>('notifications', 'enabled'),
        localDB.get<number>('notifications', 'soundVolume'),
        localDB.get<any>('notifications', 'topicSettings'),
      ]);

      this.settings = {
        enabled: enabled ?? true,
        soundVolume: volume ?? 50,
        topicSettings: topicSettings ?? {},
      };
    } catch (error) {
      console.error('Error loading notification sound settings:', error);
    }
  }

  async playNotificationSound(topic: string): Promise<void> {
    // Reload settings to ensure we have the latest
    await this.loadSettings();

    console.log('🔔 Attempting to play notification sound for topic:', topic);
    console.log('📊 Current settings:', this.settings);

    if (!this.settings) {
      console.log('❌ No settings found');
      return;
    }

    // Check if notifications are enabled globally
    if (!this.settings.enabled) {
      console.log('❌ Notifications globally disabled');
      return;
    }

    // Check if this topic exists and has sound enabled
    const topicSetting = this.settings.topicSettings[topic as NotificationTopic];
    console.log(`📋 Topic "${topic}" settings:`, topicSetting);
    
    if (!topicSetting || !topicSetting.enabled || !topicSetting.soundEnabled) {
      console.log('❌ Topic sound is disabled or topic not found');
      return;
    }

    // Check if volume is greater than 0
    if (this.settings.soundVolume === 0) {
      console.log('❌ Volume is 0');
      return;
    }

    try {
      // Use Web Audio API to create a simple notification beep
      const volumeLevel = this.settings.soundVolume / 100;
      console.log('🔊 Playing beep with volume:', volumeLevel);
      
      createNotificationBeep(volumeLevel);
      console.log('✅ Sound played successfully');
    } catch (error) {
      console.error('❌ Error playing notification sound:', error);
      // Silently fail - don't show error to user for sound issues
    }
  }

  async isTopicEnabled(topic: string): Promise<boolean> {
    await this.loadSettings();
    
    if (!this.settings || !this.settings.enabled) {
      return false;
    }

    const topicSetting = this.settings.topicSettings[topic as NotificationTopic];
    return topicSetting?.enabled ?? true;
  }
}

export const notificationSoundManager = new NotificationSoundManager();
